import { useEffect, useState, useMemo } from "react";
import {
  Form,
  Skeleton,
  Dropdown,
  Modal,
  Input,
  Button,
  Drawer,
} from "../../components/ui";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import {
  Search,
  Filter,
  Plus,
  Phone,
  Mail,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  Users,
  SearchX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGetAllParents, useUpdateParentStatus, useSearchStudents, useDeleteParent } from "../../query/parentQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { PARENTS, UPLOADS_URL } from "../../config/constants";
import { Post } from "../../config/api/post";
import { useSelector } from "react-redux";
import { generateParentUpdatePassword } from "../../config/helpers";
import StackedAvatars from "./StackedAvatars";
import DataBulkToolbar from "../../components/DataTable/DataBulkToolbar";
import DataTableCards from "../../components/DataTable/DataTableCards";
import { confirmBulkAction, runBulkIds, showBulkResult } from "../../utils/bulkActions";
import "../teachers/teachers.css";
import FilterSelect from "../../components/FilterSelect";

function statusTone(status) {
  if (status === "ACTIVE") return "is-active";
  return "is-inactive";
}

function statusLabel(status) {
  return status === "ACTIVE" ? "Active" : "Inactive";
}

const PARENT_STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

function initials(firstName = "", lastName = "") {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "P";
}

function parentName(item) {
  return `${item?.fatherFirstName || ""} ${item?.fatherLastName || ""}`.trim() || "—";
}

function motherName(item) {
  return `${item?.motherFirstName || ""} ${item?.motherLastName || ""}`.trim();
}

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function ParentManagement() {
  const token = useSelector((state) => state.user.userToken);
  const [modal2Open, setModal2Open] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState("");
  const [selectedSort, setSelectedSort] = useState("newest");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    totalDocs: 0,
    keyword: "",
    status: "",
    studentId: "",
    sort: "newest",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const navigate = useNavigate();
  const parentQueryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      status: paginationConfig.status,
      studentId: paginationConfig.studentId,
      sort: paginationConfig.sort,
    }),
    [
      paginationConfig.pageNumber,
      paginationConfig.limit,
      paginationConfig.keyword,
      paginationConfig.status,
      paginationConfig.studentId,
      paginationConfig.sort,
    ]
  );
  const { isLoading, isFetching, data: parents } = useGetAllParents(parentQueryParams);
  const { data: students } = useSearchStudents({});

  const docs = parents?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = parents?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);
  const allSelected = docs.length > 0 && docs.every((row) => selectedIds.includes(row._id));
  const hasFilters = Boolean(
    paginationConfig.status || paginationConfig.studentId || paginationConfig.sort === "oldest"
  );
  const hasQuery = Boolean(
    paginationConfig.keyword || paginationConfig.status || paginationConfig.studentId
  );

  const applyFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: selectedStatus || "",
      studentId: selectedChild || "",
      sort: selectedSort || "newest",
    }));
    setOpen(false);
  };

  const resetFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: "",
      studentId: "",
      sort: "newest",
    }));
    setOpen(false);
    setSelectedChild("");
    setSelectedStatus("");
    setSelectedSort("newest");
  };

  const onFinish = (values) => {
    if (values.newPassword && values.newPassword !== values.confirmPassword) {
      swal("Oops!", "Passwords do not match", "error");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Do you want to update the password?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then(async function (isConfirm) {
      if (isConfirm) {
        try {
          Post(
            PARENTS.resetParentPassword + selectedUser,
            { password: values.newPassword || undefined },
            token
          )
            .then((response) => {
              if (response?.data?.status) {
                const generated = response?.data?.data?.password;
                const lines = ["Password updated successfully"];
                if (generated) lines.push(`Password: ${generated}`);
                swal("Success!", lines.join("\n"), "success");
                setModal2Open(false);
                setSelectedUser(null);
                form.resetFields();
              } else {
                swal("Oops!", response?.data?.message || response?.response?.data?.message, "error");
              }
            })
            .catch(() => {
              setSelectedUser(null);
              form.resetFields();
            });
        } catch (err) {
          console.log(err);
        }
      } else {
        setModal2Open(false);
        form.resetFields();
      }
    });
  };

  useEffect(() => {
    setPaginationConfig((prev) => {
      if (prev.keyword === debouncedKeyword) return prev;
      return {
        ...prev,
        keyword: debouncedKeyword,
        pageNumber: 1,
      };
    });
  }, [debouncedKeyword]);

  const { mutate: updateParentStatus, mutateAsync: updateParentStatusAsync, isPending: isUpdatingStatus } =
    useUpdateParentStatus();
  const { mutate: deleteParent, mutateAsync: deleteParentAsync } = useDeleteParent();
  const [isBulkRunning, setIsBulkRunning] = useState(false);

  const handleStatus = (selected, status, current) => {
    if (!selected || status === current) return;
    updateParentStatus(
      { parentId: selected, status },
      {
        onSuccess: () => {
          swal("Success", `Status updated to ${statusLabel(status)}`, "success");
        },
        onError: (error) => {
          swal("Error", error?.message || "Failed to update status", "error");
        },
      }
    );
  };

  const handleDelete = (item) => {
    const realChildren = (item?.childrens || []).filter(
      (child) => child && (child.firstName || child.lastName || child.rollNumber)
    );
    if (realChildren.length > 0) {
      swal(
        "Cannot delete",
        "This parent still has children assigned. Reassign or remove the children first.",
        "warning"
      );
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Do you want to delete this parent?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteParent(item._id, {
        onSuccess: () => {
          swal("Success", "Parent deleted successfully", "success");
        },
        onError: (error) => {
          swal("Cannot delete", error?.message || "Failed to delete parent", "error");
        },
      });
    });
  };

  const handleBulkStatus = async (status) => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: `Mark as ${statusLabel(status).toLowerCase()}`,
      count: selectedIds.length,
      entityLabel: "parents",
    });
    if (!confirmed) return;

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(selectedIds, (id) =>
      updateParentStatusAsync({ parentId: id, status })
    );
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel: "Status update", success, failed });
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: "Delete selected",
      count: selectedIds.length,
      entityLabel: "parents",
    });
    if (!confirmed) return;

    let skipped = 0;
    const deletableIds = [];
    selectedIds.forEach((id) => {
      const item = docs.find((row) => row._id === id);
      const realChildren = (item?.childrens || []).filter(
        (child) => child && (child.firstName || child.lastName || child.rollNumber)
      );
      if (realChildren.length > 0) {
        skipped += 1;
        return;
      }
      deletableIds.push(id);
    });

    if (!deletableIds.length) {
      swal("Cannot delete", "Selected parents still have children assigned.", "warning");
      return;
    }

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(deletableIds, deleteParentAsync);
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel: "Delete", success, failed, skipped });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: page,
    }));
  };

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...docs.map((row) => row._id)])));
    } else {
      const pageIds = new Set(docs.map((row) => row._id));
      setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
    }
  };

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => id !== item)));
  };

  useEffect(() => {
    if (parents) {
      setPaginationConfig((prev) => ({
        ...prev,
        totalDocs: parents.totalDocs || 0,
      }));
    }
  }, [parents]);

  useEffect(() => {
    if (!open) return;
    setSelectedStatus(paginationConfig.status || "");
    setSelectedChild(paginationConfig.studentId || "");
    setSelectedSort(paginationConfig.sort || "newest");
  }, [open, paginationConfig.status, paginationConfig.studentId, paginationConfig.sort]);

  return (
    <div className="data-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>

          <button
            type="button"
            className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
            aria-label="Filter"
            onClick={() => setOpen(true)}
          >
            <Filter size={18} />
            {hasFilters ? <span className="data-filter-dot" /> : null}
          </button>

          <DataBulkToolbar
            selectedCount={selectedIds.length}
            onClear={() => setSelectedIds([])}
            disabled={isBulkRunning || isUpdatingStatus}
            actions={[
              { key: "active", label: "Mark as active", onClick: () => handleBulkStatus("ACTIVE") },
              { key: "inactive", label: "Mark as inactive", onClick: () => handleBulkStatus("INACTIVE") },
              { key: "delete", label: "Delete selected", tone: "danger", onClick: handleBulkDelete },
            ]}
          />
        </div>

        <div className="data-toolbar-actions">
          <button type="button" className="data-primary" onClick={() => navigate("/parents/addParent")}>
            <Plus size={18} />
            New Parent
          </button>
        </div>
      </div>

      <div className="data-card">
        {isLoading && !parents ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : docs.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {hasQuery ? <SearchX size={36} strokeWidth={1.6} /> : <Users size={36} strokeWidth={1.6} />}
            </span>
            <h3>{hasQuery ? "No parents found" : "No parents yet"}</h3>
            <p>
              {hasQuery
                ? "No parents match your search or filters. Try a different keyword or clear filters."
                : "Add a parent to see them listed here."}
            </p>
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching || isUpdatingStatus}>
              <table className="data-table has-row-check">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="data-check"
                        checked={allSelected}
                        onChange={(e) => toggleAll(e.target.checked)}
                      />
                    </th>
                    <th>Name</th>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Children</th>
                    <th>City</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((item) => {
                    const selected = selectedIds.includes(item._id);
                    const fullName = parentName(item);
                    const mother = motherName(item);
                    const actionItems = [
                      {
                        key: "1",
                        label: <div style={{ padding: "8px 12px" }}>View</div>,
                        onClick: () => navigate(`/parents/${item._id}`),
                      },
                      {
                        key: "2",
                        label: <div style={{ padding: "8px 12px" }}>Edit</div>,
                        onClick: () => navigate(`/parents/edit/${item._id}`),
                      },
                      {
                        key: "3",
                        label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Delete</div>,
                        onClick: () => handleDelete(item),
                      },
                    ];

                    return (
                      <tr key={item._id} className={selected ? "is-selected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            className="data-check"
                            checked={selected}
                            onChange={(e) => toggleRow(item._id, e.target.checked)}
                          />
                        </td>
                        <td>
                          <div className="data-person">
                            {item.image ? (
                              <img className="data-avatar" src={`${UPLOADS_URL}/${item.image}`} alt={fullName} />
                            ) : (
                              <span className="data-avatar">
                                {initials(item.fatherFirstName, item.fatherLastName)}
                              </span>
                            )}
                            <div>
                              <p className="data-name">{fullName}</p>
                              {mother ? <p className="data-sub">{mother}</p> : null}
                            </div>
                          </div>
                        </td>
                        <td className="data-id">#{item.parentId || "—"}</td>
                        <td>{item.createdAt ? dayjs(item.createdAt).format("MMMM D, YYYY") : "—"}</td>
                        <td>
                          <StackedAvatars
                            items={item.childrens}
                            onItemClick={(child) => child?._id && navigate(`/children/${child._id}`)}
                          />
                        </td>
                        <td>{item.city || "—"}</td>
                        <td>
                          <div className="data-contact">
                            {item.phone ? (
                              <a href={`tel:${item.phone}`} aria-label="Call">
                                <Phone size={14} />
                              </a>
                            ) : (
                              <span aria-hidden="true">
                                <Phone size={14} />
                              </span>
                            )}
                            {item.email ? (
                              <a href={`mailto:${item.email}`} aria-label="Email">
                                <Mail size={14} />
                              </a>
                            ) : (
                              <span aria-hidden="true">
                                <Mail size={14} />
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <Dropdown
                            menu={{
                              items: PARENT_STATUSES.map((option) => ({
                                key: option.value,
                                label: (
                                  <div className="data-status-option">
                                    <span className={`data-status-dot ${statusTone(option.value)}`} />
                                    {option.label}
                                    {item.status === option.value ? <Check size={14} /> : null}
                                  </div>
                                ),
                                onClick: () => handleStatus(item._id, option.value, item.status),
                              })),
                            }}
                            trigger={["click"]}
                            placement="bottomLeft"
                          >
                            <button
                              type="button"
                              className={`data-pill data-status-trigger ${statusTone(item.status)}`}
                              aria-label="Change status"
                            >
                              {statusLabel(item.status)}
                              <ChevronDown size={12} />
                            </button>
                          </Dropdown>
                        </td>
                        <td>
                          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
                            <button type="button" className="data-action" aria-label="More actions">
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} from {totalDocs} data
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Sort by</p>
          <FilterSelect
            value={selectedSort}
            onChange={setSelectedSort}
            placeholder="Select order"
            options={[
              { value: "newest", label: "Newest" },
              { value: "oldest", label: "Oldest" },
            ]}
          />
        </div>

        <div className="bw-filter-field">
          <p className="bw-filter-label">Status</p>
          <FilterSelect
            multiple
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="All statuses"
            options={[
              { value: "", label: "All statuses" },
              { value: "ACTIVE", label: "Active" },
              { value: "INACTIVE", label: "Inactive" },
            ]}
          />
        </div>

        <div className="bw-filter-field">
          <p className="bw-filter-label">Children</p>
          <FilterSelect
            multiple
            searchable
            searchPlaceholder="Search children..."
            value={selectedChild}
            onChange={setSelectedChild}
            placeholder="All children"
            options={[
              { value: "", label: "All children" },
              ...(students || []).map((student) => ({
                value: student._id,
                label: `${student.firstName || ""} ${student.lastName || ""}`.trim() +
                  (student.rollNumber ? ` (${student.rollNumber})` : ""),
              })),
            ]}
          />
        </div>
      </Drawer>

      <Modal
        title="Update Password"
        open={modal2Open}
        onCancel={() => setModal2Open(false)}
        footer={[
          <Button
            key="delete"
            danger
            onClick={() => {
              setModal2Open(false);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="cancel" onClick={() => generateParentUpdatePassword(form)}>
            Generate
          </Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            Update Password
          </Button>,
        ]}
        centered
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label="New Password" name="newPassword">
            <Input.Password size="large" placeholder="Leave blank to auto-generate" />
          </Form.Item>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const next = getFieldValue("newPassword");
                  if (!next && !value) return Promise.resolve();
                  if (!value) {
                    return Promise.reject(new Error("Please confirm the new password"));
                  }
                  if (next === value) return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ParentManagement;
